import { useTranslation } from "react-i18next";
import type { HandoffConfirmation, HandoffSignedInNotice } from "../lib/useWebHandoff";
import styles from "./HandoffScreens.module.scss";

/**
 * «Вы вошли как … Не вы? Выйти ×» — over any page, after a code from the app
 * seated someone in an account in this browser. A link with someone else's
 * code would otherwise leave a visitor in that account without a word; here
 * they see whose it is and can leave it with one tap.
 */
export function HandoffSignedInBar({ who, dismiss, notMe }: HandoffSignedInNotice) {
    const { t } = useTranslation();
    return (
        <div className={styles.notice} role="status" aria-live="polite">
            <p className={styles.noticeBody}>
                <span className={styles.noticeText}>
                    {t("trainingBasis.handoff.signedInAs", { who, defaultValue: "You're signed in as {{who}}." })}
                </span>
                <button type="button" className={styles.noticeOut} onClick={notMe}>
                    {t("trainingBasis.handoff.notMe", { defaultValue: "Not you? Sign out" })}
                </button>
            </p>
            <button
                type="button"
                className={styles.noticeClose}
                onClick={dismiss}
                aria-label={t("trainingBasis.handoff.dismiss", { defaultValue: "Close" })}
            >
                <span aria-hidden="true">×</span>
            </button>
        </div>
    );
}

/** The moment between the tap on the watch and the page: the code is being exchanged. */
export function HandoffLoader() {
    const { t } = useTranslation();
    return (
        <div className={styles.screen} role="status" aria-live="polite">
            <p className={styles.loading}>{t("trainingBasis.signingIn", { defaultValue: "Signing you in…" })}</p>
        </div>
    );
}

/**
 * Another person is signed in in this browser, and the link from the app
 * names someone else. Switching silently would let anyone who hands out a
 * link with their own code seat a visitor in their account; so the person
 * sees both names and decides. The focus starts on «Остаться», the answer
 * that changes nothing (Enter, a screen reader).
 */
export function HandoffConfirm({ current, next, accept, stay }: HandoffConfirmation) {
    const { t } = useTranslation();
    const someone = t("trainingBasis.handoff.someoneElse", { defaultValue: "another account" });
    return (
        <div className={styles.screen}>
            <div
                className={styles.card}
                role="dialog"
                aria-modal="true"
                aria-labelledby="handoff-confirm-title"
                aria-describedby="handoff-confirm-hint"
            >
                <h1 id="handoff-confirm-title" className={styles.title}>
                    {t("trainingBasis.handoff.title", { defaultValue: "Switch accounts on the site?" })}
                </h1>
                <dl className={styles.who}>
                    <div className={styles.row}>
                        <dt>{t("trainingBasis.handoff.current", { defaultValue: "Signed in now" })}</dt>
                        <dd>{current || someone}</dd>
                    </div>
                    <div className={styles.row}>
                        <dt>{t("trainingBasis.handoff.next", { defaultValue: "Link from the app" })}</dt>
                        <dd>{next}</dd>
                    </div>
                </dl>
                <p id="handoff-confirm-hint" className={styles.hint}>
                    {t("trainingBasis.handoff.hint", {
                        defaultValue: "If you don't recognise the second account, don't switch — stay in yours.",
                    })}
                </p>
                <div className={styles.actions}>
                    <button type="button" className={styles.primary} onClick={accept}>
                        {t("trainingBasis.handoff.switch", { defaultValue: "Switch account" })}
                    </button>
                    <button type="button" className={styles.secondary} onClick={stay} autoFocus>
                        {t("trainingBasis.handoff.stay", { defaultValue: "Stay" })}
                    </button>
                </div>
            </div>
        </div>
    );
}
